import React, { useState, useContext, useEffect } from 'react';
import { 
  Card, CardContent, CardHeader, 
  CardTitle, CardDescription, CardFooter 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserRole } from '@/context/auth/types';
import { SmsCustomer } from '@/hooks/sms/types';
import { 
  User, Phone, Mail, CalendarClock, ShoppingCart, 
  MessageSquare, AlertTriangle, CheckCircle, XCircle,
  DollarSign, ChevronLeft, Clock, RefreshCw
} from 'lucide-react';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter, DialogTrigger
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getOrderRecency } from './utils';
import { toggleSubscriptionStatus } from '@/services/smsApi';
import { toast } from '@/components/ui/use-toast';
import { useRestaurantContext } from '@/context/RestaurantContext';
import { useCustomerOrders } from '@/hooks/useCustomerOrders';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatLastOrderDate, formatOrderDateTime } from '@/utils/date-helpers';

interface SubscriberDetailsProps {
  subscriber: SmsCustomer;
  onBack: () => void;
  onUpdate: (subscriberId?: string) => void;
  userRole?: UserRole;
}

const SubscriberDetails: React.FC<SubscriberDetailsProps> = ({
  subscriber,
  onBack,
  onUpdate,
  userRole
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  // Add local state to immediately update UI before backend refresh completes
  const [localSubscriber, setLocalSubscriber] = useState<SmsCustomer>(subscriber);
  // Add state to track optimistic updates
  const [optimisticStatusUpdate, setOptimisticStatusUpdate] = useState<string | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(0);
  const [lastSubscriberId, setLastSubscriberId] = useState<string>(subscriber.id);
  const [lastRestaurantId, setLastRestaurantId] = useState<string | null>(null);

  // Get active restaurant from context
  const { activeRestaurantDetails } = useRestaurantContext();

  // Keep local state in sync with props while respecting optimistic updates
  useEffect(() => {
    // Check if we're looking at a different subscriber or if restaurant changed
    const subscriberChanged = subscriber.id !== lastSubscriberId;
    const restaurantChanged = activeRestaurantDetails?.id !== lastRestaurantId;

    // Also check if the incoming subscriber status is different (restaurant-specific change)
    const statusChanged = subscriber.status !== localSubscriber.status && !optimisticStatusUpdate;

    if (subscriberChanged || restaurantChanged || statusChanged) {
      // Clear optimistic update when subscriber, restaurant, or status changes
      setOptimisticStatusUpdate(null);
      setLastSubscriberId(subscriber.id);
      setLastRestaurantId(activeRestaurantDetails?.id || null);
      setLocalSubscriber(subscriber);
      console.log('[SUBSCRIBER-DETAILS] Update detected, resetting local state', {
        subscriberChanged,
        restaurantChanged,
        statusChanged,
        newStatus: subscriber.status,
        oldStatus: localSubscriber.status,
        restaurantId: activeRestaurantDetails?.id
      });
    } else if (optimisticStatusUpdate) {
      // Only preserve optimistic update if we're still looking at the same subscriber AND restaurant
      // and the update was recent (within last 10 seconds)
      const timeSinceUpdate = Date.now() - lastUpdateTime;
      if (timeSinceUpdate < 10000) {
        // Keep our optimistic status but take all other properties from the incoming data
        setLocalSubscriber(prev => ({
          ...subscriber,
          status: optimisticStatusUpdate
        }));
        console.log('[SUBSCRIBER-DETAILS] Preserving recent optimistic status update:', optimisticStatusUpdate);
      } else {
        // Clear stale optimistic update
        setOptimisticStatusUpdate(null);
        setLocalSubscriber(subscriber);
        console.log('[SUBSCRIBER-DETAILS] Clearing stale optimistic update');
      }
    } else {
      // If no optimistic update is in progress, use the subscriber data directly
      setLocalSubscriber(subscriber);
    }
  }, [subscriber, optimisticStatusUpdate, lastSubscriberId, lastUpdateTime, activeRestaurantDetails?.id, lastRestaurantId]);
  
  const canModifySubscription = 
    userRole === UserRole.SUPER_ADMIN || 
    userRole === UserRole.ORG_ADMIN || 
    userRole === UserRole.REST_ADMIN;
  
  // Fetch customer orders using the new hook
  const { orders, loading: ordersLoading, error: ordersError, refetch: refetchOrders } = useCustomerOrders(subscriber.email);
  
  // Format status text
  const formatStatus = (status?: string) => {
    switch (status) {
      case 'subscribed': return 'Subscribed';
      case 'unsubscribed': return 'Unsubscribed';
      case 'invalid': return 'Invalid';
      default: return 'Unknown';
    }
  };
  
  // Get status color class
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'subscribed': return 'bg-ubereats-green hover:bg-ubereats-green/90 text-brand-white';
      case 'unsubscribed': return 'bg-brand-yellow hover:bg-brand-yellow/90 text-brand-dark-text';
      case 'invalid': return 'bg-brand-red hover:bg-brand-red/90 text-brand-white';
      default: return 'bg-brand-grey hover:bg-brand-grey/90 text-brand-dark-text';
    }
  };
  
  // Get order recency classes
  const getOrderRecencyClass = (lastOrder?: string) => {
    if (!lastOrder || lastOrder === 'Never') return 'text-muted-foreground';
    
    const recency = getOrderRecency(lastOrder);
    // Remove color classes and just return base styling
    return 'text-muted-foreground';
  };

  // Handle subscription status toggle with optimistic updates and minimal refreshes
  const handleStatusToggle = async () => {
    setIsUpdating(true);
    
    try {
      if (!activeRestaurantDetails || !activeRestaurantDetails.id) {
        throw new Error('No restaurant selected');
      }
      
      // Determine new status
      const newStatus = localSubscriber.status !== 'subscribed';
      const newStatusText = newStatus ? 'subscribed' : 'unsubscribed';
      
      // Update the local state immediately for responsive UI
      setLocalSubscriber(prev => ({
        ...prev,
        status: newStatusText
      }));
      
      // Set our optimistic update flag and timestamp
      setOptimisticStatusUpdate(newStatusText);
      setLastUpdateTime(Date.now());
      
      // Call the API to toggle subscription status
      const result = await toggleSubscriptionStatus(
        localSubscriber.id,
        newStatus,
        activeRestaurantDetails.id
      );
      
      // Always close the dialog regardless of result
      setIsDialogOpen(false);
      
      // Handle different response scenarios
      if (result.success && !result.partial_success) {
        // Full success case - just show toast and trust optimistic update
        toast({
          title: newStatus ? 'Subscription Enabled' : 'Subscription Disabled',
          description: `Customer has been ${newStatus ? 'subscribed' : 'unsubscribed'} successfully.`,
          variant: 'default'
        });
        
        // No refresh needed - trust our optimistic update
      } else if (result.partial_success) {
        // Only for partial success - show warning and refresh
        toast({
          title: 'Partial Success',
          description: (
            <div className="space-y-2">
              <p>{result.message || `Some operations completed but others failed.`}</p>
              {result.details?.successes && result.details.successes.length > 0 && (
                <div className="text-xs">
                  <span className="font-semibold">Completed:</span>{' '}
                  {result.details.successes.join(', ')}
                </div>
              )}
              {result.details?.errors && result.details.errors.length > 0 && (
                <div className="text-xs text-amber-600">
                  <span className="font-semibold">Issues:</span>{' '}
                  {result.details.errors.join(', ')}
                </div>
              )}
            </div>
          ),
          variant: 'default'
        });
        
        // Only refresh on partial success
        setTimeout(() => {
          onUpdate(localSubscriber.id);
        }, 500);
      } else {
        // Full failure but with structured response
        throw new Error(result.message || 'Failed to update subscription status');
      }
      
    } catch (error: any) {
      console.error('Failed to update subscription status:', error);
      
      // Revert the local state change since the operation failed
      setLocalSubscriber(subscriber);
      
      // Clear the optimistic update flag immediately
      setOptimisticStatusUpdate(null);
      
      // Extract detailed information if available
      const errorDetails = error.operationDetails || {};
      const errorMessage = error.message || 'An unknown error occurred';
      
      // Show comprehensive error message
      toast({
        title: 'Failed to Update Subscription',
        description: (
          <div className="space-y-1">
            <p>{errorMessage}</p>
            {errorDetails.retryAttempts > 0 && (
              <p className="text-xs opacity-70">System tried {errorDetails.retryAttempts} times to complete this operation.</p>
            )}
            <p className="text-xs opacity-70">
              You can try again or contact support if this issue persists.
            </p>
          </div>
        ),
        variant: 'destructive'
      });
      
      // Refresh on error to ensure consistent state
      setTimeout(() => {
        onUpdate(localSubscriber.id);
      }, 500);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <CardHeader className="bg-muted/50">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage 
                  src={`https://api.dicebear.com/7.x/initials/svg?seed=${subscriber.name || subscriber.phone}`} 
                />
                <AvatarFallback>
                  <User className="h-8 w-8" />
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-xl">{localSubscriber.name || 'Unknown Customer'}</CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Badge 
                    variant={localSubscriber.status === 'subscribed' ? "default" : "secondary"}
                    className={getStatusColor(localSubscriber.status)}
                  >
                    {formatStatus(localSubscriber.status)}
                  </Badge>
                  
                  {localSubscriber.lastOrder && (
                    <span className="text-sm">
                      <span className={getOrderRecencyClass(localSubscriber.lastOrder)} title={`Last order: ${formatLastOrderDate(localSubscriber.lastOrder)}`}>
                        <ShoppingCart className="h-3.5 w-3.5 inline mr-1" />
                        {formatLastOrderDate(localSubscriber.lastOrder)}
                      </span>
                    </span>
                  )}
                </CardDescription>
              </div>
            </div>
            
            {canModifySubscription && (
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    variant={localSubscriber.status === 'subscribed' ? 'outline' : 'default'}
                    className={localSubscriber.status !== 'subscribed' ? 'bg-ubereats-green hover:bg-ubereats-green/90 text-brand-white [&_svg]:text-white' : ''}
                  >
                    {localSubscriber.status === 'subscribed' ? 'Unsubscribe' : 'Subscribe'} 
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {localSubscriber.status === 'subscribed' 
                        ? 'Unsubscribe Customer' 
                        : 'Subscribe Customer'}
                    </DialogTitle>
                    <DialogDescription>
                      {localSubscriber.status === 'subscribed' 
                        ? 'This will opt the customer out of SMS communications.' 
                        : 'This will opt the customer into SMS communications.'}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="py-3">
                    {localSubscriber.status === 'subscribed' ? (
                      <Alert variant="default">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          Manually unsubscribing a customer will override their preference. They will no longer receive SMS messages.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Alert variant="default">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          Manually subscribing a customer may violate regulations. Ensure you have proper consent.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleStatusToggle}
                      disabled={isUpdating}
                      variant={localSubscriber.status === 'subscribed' ? 'destructive' : 'default'}
                    >
                      {isUpdating ? 'Processing...' : 
                        localSubscriber.status === 'subscribed' ? 'Unsubscribe' : 'Subscribe'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="messages">Messages</TabsTrigger>
              <TabsTrigger value="orders">Orders & Activity</TabsTrigger>
            </TabsList>
            
            <TabsContent value="profile" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Phone className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{subscriber.phone || 'No phone'}</div>
                        <div className="text-sm text-muted-foreground">Primary Phone</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Mail className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{subscriber.email || 'No email'}</div>
                        <div className="text-sm text-muted-foreground">Primary Email</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Subscription Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {localSubscriber.status === 'subscribed' && (
                      <div className="flex items-start gap-3">
                        <CalendarClock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{formatLastOrderDate(localSubscriber.opt_in_date) || 'Unknown'}</div>
                          <div className="text-sm text-muted-foreground">Subscription Date</div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-start gap-3">
                      <MessageSquare className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{localSubscriber.opt_in_source || 'Unknown'}</div>
                        <div className="text-sm text-muted-foreground">Opt-in Source</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Separator />
              
              <div>
                <h3 className="text-lg font-medium mb-3">Customer Notes</h3>
                <p className="text-muted-foreground">
                  No notes available for this customer.
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="messages" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Message History</CardTitle>
                  <CardDescription>
                    Track all SMS messages sent to this customer
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border border-dashed border-muted-foreground/25">
                    <div className="p-12 text-center">
                      <MessageSquare className="mx-auto h-12 w-12 text-muted-foreground opacity-25" />
                      <h3 className="mt-4 text-lg font-semibold text-muted-foreground">Coming Soon</h3>
                      <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto">
                        Message history tracking is currently in development. Check back soon to view all SMS communications with this customer.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="orders" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Total Orders</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-3xl font-bold flex items-center">
                      <ShoppingCart className="h-5 w-5 mr-2 text-muted-foreground" />
                      {localSubscriber.totalOrders || 0}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Last order: {formatLastOrderDate(localSubscriber.lastOrder) || 'Never'}
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Total Spend</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-3xl font-bold flex items-center">
                      <DollarSign className="h-5 w-5 mr-2 text-muted-foreground" />
                      {typeof localSubscriber.totalSpend === 'number'
                        ? localSubscriber.totalSpend.toFixed(2)
                        : parseFloat(localSubscriber.totalSpend || '0').toFixed(2)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Avg. order: $
                      {localSubscriber.totalOrders && localSubscriber.totalOrders > 0 && localSubscriber.totalSpend
                        ? (parseFloat(localSubscriber.totalSpend.toString()) / localSubscriber.totalOrders).toFixed(2)
                        : '0.00'}
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Loyalty Status</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="text-xl font-bold flex items-center">
                      {localSubscriber.loyaltyTier || 'Standard'}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Based on ordering patterns
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Order History</CardTitle>
                    <CardDescription>
                      Recent orders from restaurants you have access to
                    </CardDescription>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={refetchOrders}
                    disabled={ordersLoading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-1 ${ordersLoading ? 'animate-spin' : ''}`} />
                    {ordersLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                </CardHeader>
                <CardContent>
                  {ordersLoading ? (
                    <div className="rounded-md border">
                      <div className="p-8 text-center">
                        <RefreshCw className="mx-auto h-12 w-12 text-muted-foreground opacity-50 animate-spin" />
                        <h3 className="mt-3 text-lg font-semibold">Loading Orders</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Fetching order history...
                        </p>
                      </div>
                    </div>
                  ) : ordersError ? (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="flex flex-col gap-2">
                          <p>Error loading orders: {ordersError}</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={refetchOrders} 
                            className="w-fit"
                          >
                            Try Again
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : orders.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Order ID</TableHead>
                            <TableHead>Restaurant</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Date & Time</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {orders.map((order) => (
                            <TableRow key={order.pumpd_order_id}>
                              <TableCell className="font-medium">
                                {order.pumpd_order_id.substring(0, 8)}...
                              </TableCell>
                              <TableCell>{order.restaurant_name}</TableCell>
                              <TableCell>{order.service_type}</TableCell>
                              <TableCell>
                                {formatOrderDateTime(order.date_and_time)}
                              </TableCell>
                              <TableCell className="text-right">
                                ${order.cart_value.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="rounded-md border">
                      <div className="p-8 text-center">
                        <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
                        <h3 className="mt-3 text-lg font-semibold">No Orders Found</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {!subscriber.email 
                            ? 'No email address on file for this customer'
                            : 'No orders found in restaurants you have access to'}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
        
        <CardFooter className="bg-muted/50 px-6 py-3 flex justify-between">
          <Button variant="outline" onClick={onBack}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          
          <div className="flex gap-2">
            {canModifySubscription && localSubscriber.status === 'subscribed' && (
              <Button variant="outline">
                <MessageSquare className="h-4 w-4 mr-2" />
                Send Message
              </Button>
            )}
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default SubscriberDetails;
